import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../components/feature/SEOHead';

const perks = [
  {
    icon: 'ri-flashlight-line',
    tag: 'EXCLUSIVE',
    title: 'Breaking News First',
    desc: 'Get notified before it hits social media. Stay ahead of the entire community.',
  },
  {
    icon: 'ri-sword-line',
    tag: 'WEEKLY',
    title: 'Weapon Tier Lists',
    desc: 'Meta breakdowns and exclusive weapon guides from top-ranked players.',
  },
  {
    icon: 'ri-trophy-line',
    tag: 'CDL',
    title: 'Esports Recaps',
    desc: 'Full CDL coverage — match results, standings, and in-depth analysis.',
  },
  {
    icon: 'ri-gift-line',
    tag: 'PERKS',
    title: 'Community Giveaways',
    desc: 'Subscriber-only access to operator bundle and gift card giveaways.',
  },
];

const newsletterSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'CoD Newsletter — Subscribe for Weekly Intel | CallOfDutyNext.com',
    description:
      'Join 50,000+ Call of Duty operators. Get weekly CoD intel, weapon tier lists, CDL esports recaps, and exclusive giveaways — free.',
    url: `${SITE_URL}/newsletter`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Newsletter', item: `${SITE_URL}/newsletter` },
    ],
  },
];

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-block font-['Barlow_Condensed'] font-bold text-xs tracking-wider text-white bg-[#2a2a2a] border border-[#3a3a3a] px-2 py-0.5">
      {label}
    </span>
  );
}

export default function NewsletterPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const data = new URLSearchParams();
    data.append('email', email);
    data.append('name', name);
    fetch('https://readdy.ai/api/form/d70pm2pqa83q0hmd1f0g', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => { setSubmitted(true); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const inputClass = "w-full bg-[#0a0a0a] border border-[#222] focus:border-[#444] text-white text-sm px-4 py-3 outline-none placeholder-[#444] transition-colors";

  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title="CoD Newsletter — Weekly Intel for 50K+ Operators | CallOfDutyNext.com"
        description="Subscribe free to the CallOfDutyNext.com newsletter. Get weekly Call of Duty intel, weapon meta, CDL esports coverage, and subscriber-only giveaways delivered to your inbox."
        keywords="Call of Duty newsletter, CoD weekly update, Warzone meta newsletter, CDL esports digest"
        canonical="/newsletter"
        schema={newsletterSchema}
      />
      <Navbar />

      <div className="pt-14">
        {/* ── Title bar ─────────────────────────── */}
        <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <h1 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase tracking-wider leading-none">
              Newsletter
            </h1>
            <p className="text-[#555] text-sm mt-1">
              50,000+ operators already subscribed. Weekly CoD intel, meta analysis, and exclusive content.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-10">

            {/* ── LEFT: Perks ──────────────────────── */}
            <div className="lg:col-span-2 mb-10 lg:mb-0">
              {/* Perks grid */}
              <div className="flex items-center gap-3 mb-6">
                <h2 className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase tracking-widest">
                  What You Get
                </h2>
                <div className="flex-1 h-px bg-[#1e1e1e]" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border border-[#1e1e1e] mb-10">
                {perks.map((perk, i) => (
                  <div
                    key={perk.title}
                    className={`p-6 ${i % 2 === 0 ? 'border-r border-[#1e1e1e]' : ''} ${i < 2 ? 'border-b border-[#1e1e1e]' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-8 h-8 flex items-center justify-center text-[#00ff41]">
                        <i className={`${perk.icon} text-xl`} />
                      </div>
                      <Tag label={perk.tag} />
                    </div>
                    <h3 className="font-['Barlow_Condensed'] font-black text-white text-lg uppercase mb-2">
                      {perk.title}
                    </h3>
                    <p className="text-[#666] text-sm leading-relaxed">{perk.desc}</p>
                  </div>
                ))}
              </div>

              {/* Social proof */}
              <div className="border border-[#1e1e1e] bg-[#0d0d0d] p-6">
                <div className="grid grid-cols-3 divide-x divide-[#1e1e1e] text-center">
                  {[
                    { value: '50K+', label: 'Subscribers' },
                    { value: 'Weekly', label: 'Dispatch' },
                    { value: 'Free', label: 'Always' },
                  ].map((s) => (
                    <div key={s.label} className="py-2">
                      <div className="font-['Barlow_Condensed'] font-black text-[#00ff41] text-2xl leading-none">{s.value}</div>
                      <div className="text-[#555] text-xs tracking-wider uppercase mt-1">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── RIGHT: Signup form ───────────────── */}
            <div className="lg:col-span-1">
              <div className="border border-[#1e1e1e] bg-[#0d0d0d] sticky top-20">
                <div className="px-6 py-4 border-b border-[#1e1e1e]">
                  <h2 className="font-['Barlow_Condensed'] font-black text-white text-sm uppercase tracking-widest">
                    Subscribe Now
                  </h2>
                </div>

                {submitted ? (
                  <div className="text-center py-16 px-6">
                    <i className="ri-checkbox-circle-line text-[#00ff41] text-5xl mb-4 block" />
                    <h3 className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase mb-2">
                      You&apos;re In!
                    </h3>
                    <p className="text-[#666] text-sm">Check your inbox. First dispatch drops this Friday.</p>
                  </div>
                ) : (
                  <form
                    id="newsletter-page-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="p-6 space-y-4"
                  >
                    <div>
                      <label className="block text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
                        Your Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Operator Ghost"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#00ff41] hover:bg-[#00e63a] text-black font-['Barlow_Condensed'] font-black text-sm tracking-widest py-4 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                    >
                      {loading ? 'SUBSCRIBING...' : 'SUBSCRIBE & GET INTEL'}
                    </button>
                    <p className="text-[#444] text-xs text-center">
                      Free. No spam. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
